// App.jsx
import React from 'react';
import { Routes, Route } from 'react-router-dom';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import Axios from 'axios';
import ReactGA from 'react-ga4';
import { useAuth0 } from '@auth0/auth0-react';
import { withRouter } from './withRouter';

import Sidebar from './clever_components/Sidebar';
import Header from './clever_components/Header';
import Main from './main';
import Users from './users/Users';

import './tools/setupCharts';

import {
  ConnectedUserContext,
  buildCtxValue,
} from './connectedUser-context';

const CST_GA_DATASTREAM_ID = 'G-5Y1NW7R1G7';
const CST_COOKIECONSENT_DEBUG = false;

const SESSION_CONNECTION_KEY = 'statelo_session_connection_done';
const LAST_AUTH_UPDATED_KEY = 'statelo_last_auth_updated';

const isIE = () => {
  const ua = window.navigator.userAgent;
  return ua.includes('MSIE') || ua.includes('Trident/');
};

class App extends React.Component {
  static contextType = ConnectedUserContext;

  constructor(props) {
    super(props);
    this.state = {
      connectedUser: null,
      appLoading: true,
      consentSet: false,
    };
  }

  setConnectedUser = (cu) => this.setState({ connectedUser: cu });

  refreshConnectedUser = (clsIds, source, callback) => {
    const { auth0, navigate } = this.props;
    const authUser = auth0?.user;
    if (!authUser) return;

    this.setState({ appLoading: true });
    Users.getDbFullUserData(authUser, clsIds, (cu) => {
      this.setConnectedUser(cu);

      if (!cu?.exists) {
        navigate('/profile');
      }

      this.setState({ appLoading: false }, () => {
        if (callback) callback(); // 🔹 exécute le callback une fois le refresh fini
      });
    });
  };

  componentDidMount() {
    const { auth0 } = this.props;

    if (getCookieConsentValue('cookieConsent') === undefined) {
      Axios.post('/api/consentEvents/incBannerCount', { counter: 'bannerDisplayed' });
    }
    if (getCookieConsentValue('cookieConsent') === 'true') {
      ReactGA.initialize(CST_GA_DATASTREAM_ID);
    }

    if (!auth0?.isLoading && auth0?.isAuthenticated && auth0?.user && !this.state.connectedUser) {
      this.refreshConnectedUser(null, 'app');
    }
  }

  componentDidUpdate(prevProps) {
    const { location, auth0 } = this.props;

    if (location?.pathname !== prevProps.location?.pathname && getCookieConsentValue('cookieConsent') === 'true') {
      ReactGA.initialize(CST_GA_DATASTREAM_ID);
      window.scrollTo(0, 0);
    }

    // NEW --- reset si l'utilisateur vient de se déconnecter
    const justLoggedOut =
      prevProps.auth0?.isAuthenticated &&
      !auth0?.isAuthenticated;

    if (justLoggedOut) {
      sessionStorage.removeItem(SESSION_CONNECTION_KEY);
      sessionStorage.removeItem(LAST_AUTH_UPDATED_KEY);
    }

    const authJustLoaded =
      prevProps.auth0?.isLoading &&
      !auth0?.isLoading &&
      auth0?.isAuthenticated &&
      auth0?.user &&
      !this.state.connectedUser;

    if (authJustLoaded) {
      // this.refreshConnectedUser(null, 'app');
      this.refreshConnectedUser(null, 'app', () => {

        // NEW
        const userId = this.state.connectedUser?.user?._id;
        const updatedAt = auth0.user?.updated_at;
        const lastUpdated = sessionStorage.getItem(LAST_AUTH_UPDATED_KEY);
        const alreadySet = sessionStorage.getItem(SESSION_CONNECTION_KEY);

        // ✅ vrai login si flag manquant ou "updated_at" différent
        const isNewLogin = !alreadySet || updatedAt !== lastUpdated;

        if (userId && isNewLogin) {
          Users.setConnection(userId);
          sessionStorage.setItem(SESSION_CONNECTION_KEY, 'true');
          sessionStorage.setItem(LAST_AUTH_UPDATED_KEY, updatedAt);
        }


      });

    }
  }

  handleAcceptCookies = () => {
    ReactGA.initialize(CST_GA_DATASTREAM_ID);
    Axios.post('/api/consentEvents/incBannerCount', { counter: 'consentGranted' });

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'update', { analytics_storage: 'granted' });

    this.setState({ consentSet: true });
  };

  handleDeclineCookies = () => {
    Axios.post('/api/consentEvents/incBannerCount', { counter: 'consentDenied' });

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'update', { analytics_storage: 'denied' });

    this.setState({ consentSet: true });
  };

  render() {
    // Construit la value de contexte avec buildCtxValue (comme tu voulais)
    const ctxValue = buildCtxValue(
      this.state.connectedUser,
      this.setConnectedUser,
      this.refreshConnectedUser
    );

    return (
      <ConnectedUserContext.Provider value={ctxValue}>
        {isIE() ? (
          <div>
            <h4 className="ieWarning1">IMPORTANT</h4>
            <h5 className="ieWarning2">Chez Statelo, nous utilisons des technologies avancées qui ne sont pas supportées par Internet Explorer</h5>
            <h5 className="ieWarning2">Pour une expérience immersive et novatrice, nous vous suggérons d'utiliser Chrome.</h5>
            <a href="https://www.google.com/intl/fr/chrome/" target="_blank" rel="noreferrer">
              <h5 className="ieWarning2">
                <img className="img-fluid" src="/img/chrome_ico.png" alt="chrome" width="50" />
              </h5>
              <h5 className="ieWarning3">Télécharger Chrome</h5>
            </a>
          </div>
        ) : (
          <div className="app">
            <Header />
            <div className="app-body">
              <Sidebar auth0={this.props.auth0} />
              <Routes>
                <Route path="/*"
                  element={ <Main refreshConnectedUser={this.refreshConnectedUser} />} />
              </Routes>
            </div>
          </div>
        )}

        <CookieConsent
          onAccept={this.handleAcceptCookies}
          onDecline={this.handleDeclineCookies}
          debug={CST_COOKIECONSENT_DEBUG}
          location="bottom"
          buttonText="Accepter"
          cookieName="cookieConsent"
          enableDeclineButton
          declineButtonText="Refuser"
          declineCookieValue={false}
          style={{ background: '#003200' }}
          buttonStyle={{ background:'#f5fff5', color: 'black', fontSize:'13px', width: '100px', borderRadius: '3px' }}
          declineButtonStyle={{ background:'#f5fff5', color: 'black', fontSize: '13px', width: '100px', borderRadius: '3px' }}
          expires={183}
        >
          Ce site utilise les témoins de navigations tel que décris dans notre <a href="/confidentialite" className="whitelink">politique de confidentialité.</a>
        </CookieConsent>
      </ConnectedUserContext.Provider>
    );
  }
}

// Petit wrapper **fonctionnel** (PAS un HOC Auth0), on garde **withRouter**:
function AppAuth0Props(props) {
  const auth0 = useAuth0();
  return <App {...props} auth0={auth0} />;
}

export default withRouter(AppAuth0Props);
